import { ComponentRenderProps, useDataValue } from '@json-render/react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

type DataTableProps = ComponentRenderProps & {
  element: {
    props: {
      dataPath: string;
      columns: Array<{
        key: string;
        label: string;
        format?: 'currency' | 'date' | 'status' | 'text';
      }>;
    };
    children?: string[];
  };
};

function formatCell(
  value: unknown,
  format?: 'currency' | 'date' | 'status' | 'text',
): string {
  if (format === 'currency' && typeof value === 'number') {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(value);
  }
  if (format === 'date' && value) {
    return String(value).split('T')[0] ?? '';
  }
  if (format === 'status' && typeof value === 'string') {
    return value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  }
  if (format === 'status' && typeof value === 'string') {
    // Match backend formatStatus: replace underscores, title-case words
    return value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  }
  return String(value ?? '');
}

export function DataTable({ element }: DataTableProps) {
  const { dataPath, columns } = element.props;
  const data = useDataValue(dataPath) as Array<Record<string, unknown>> | null;

  if (!data || data.length === 0) {
    const type = dataPath === '/invoices' ? 'invoices' : 'orders';
    return <p className="text-gray-500 italic">No {type} found.</p>;
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          {columns.map((col) => (
            <TableHead key={col.key} className="font-semibold">
              {col.label}
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {data.map((row, idx) => (
          <TableRow key={idx}>
            {columns.map((col) => (
              <TableCell key={col.key}>
                {formatCell(row[col.key], col.format)}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
